'use client'

import { ClientSelector } from '@/components/client/ClientSelector'
import { EmployeeSelector } from '@/components/EmployeeSelector'
import { HospitalSearchSelector } from '@/components/HospitalSearchSelector'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { useEntityAccess } from '@/lib/hooks/use-entity-access'
import { SupplierSelector } from '@/components/SupplierSelector'
import { UserSelector } from '@/components/UserSelector'
import { MdInfoOutline } from 'react-icons/md'

export type PayableRecipientType =
    | 'SUPPLIER'
    | 'CLIENT'
    | 'PARTNER'
    | 'PROFESSIONAL'
    | 'HOSPITAL'
    | 'USER'
    | 'EMPLOYEE'

export const TYPE_LABELS: Record<PayableRecipientType, string> = {
    SUPPLIER: 'Fornecedor',
    CLIENT: 'Cliente',
    PARTNER: 'Parceiro',
    PROFESSIONAL: 'Profissional',
    HOSPITAL: 'Hospital',
    USER: 'Usuário do sistema',
    EMPLOYEE: 'Funcionário (RH)',
}

export interface PayableRecipientValue {
    type: PayableRecipientType
    supplierId?: number | null
    supplierName?: string
    clientId?: number | null
    partnerId?: number | null
    professionalId?: number | null
    hospitalId?: number | null
    userId?: number | null
    employeeId?: number | null
}

interface Props {
    value: PayableRecipientValue
    onChange: (value: PayableRecipientValue) => void
    disabled?: boolean
}

const BANK_HINT: Partial<Record<PayableRecipientType, string>> = {
    SUPPLIER:
        'Aviso: fornecedores não possuem campos bancários no cadastro. Auto-transfer via conta digital só funciona com Parceiro, Funcionário ou Conta Bancária vinculada.',
    PROFESSIONAL:
        'Aviso: profissionais não possuem campos bancários no cadastro. Auto-transfer só funciona via Conta Bancária vinculada.',
    CLIENT:
        'Aviso: clientes geralmente são destinos de cobrança, não pagamento. Auto-transfer não está disponível para clientes.',
    HOSPITAL:
        'Aviso: hospitais não possuem campos bancários no cadastro padrão. Auto-transfer só funciona via Conta Bancária vinculada.',
    USER:
        'Para auto-transfer, o usuário precisa estar vinculado a um funcionário do RH com PIX ou dados bancários cadastrados.',
}

export function PayableRecipientPicker({
    value,
    onChange,
    disabled = false,
}: Props) {
    const { canSeeProfessionals } = useEntityAccess()
    const handleTypeChange = (newType: PayableRecipientType) => {
        // Reset: limpa todas as FKs ao trocar tipo
        onChange({
            type: newType,
            supplierId: null,
            supplierName: '',
            clientId: null,
            partnerId: null,
            professionalId: null,
            hospitalId: null,
            userId: null,
            employeeId: null,
        })
    }

    const hint = BANK_HINT[value.type]

    return (
        <div className="space-y-3">
            <div className="form-control w-full">
                <label className="label">
                    <span className="label-text font-medium">
                        Tipo de Destinatário <span className="text-error">*</span>
                    </span>
                </label>
                <select
                    className="select select-bordered w-full"
                    value={value.type}
                    onChange={(e) =>
                        handleTypeChange(e.target.value as PayableRecipientType)
                    }
                    disabled={disabled}
                >
                    {(Object.keys(TYPE_LABELS) as PayableRecipientType[])
                        // Já escolhido continua na lista, para não sumir da tela.
                        .filter(
                            (t) =>
                                t !== 'PROFESSIONAL' ||
                                canSeeProfessionals ||
                                value.type === 'PROFESSIONAL',
                        )
                        .map((t) => (
                            <option key={t} value={t}>
                                {TYPE_LABELS[t]}
                            </option>
                        ),
                    )}
                </select>
            </div>

            {value.type === 'SUPPLIER' && (
                <>
                    <SupplierSelector
                        value={value.supplierId || null}
                        onChange={(id) =>
                            onChange({ ...value, supplierId: id || null })
                        }
                        disabled={disabled}
                    />
                    {!value.supplierId && (
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">
                                    Ou informe o nome (sem cadastro)
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Nome do fornecedor"
                                value={value.supplierName || ''}
                                onChange={(e) =>
                                    onChange({
                                        ...value,
                                        supplierName: e.target.value,
                                    })
                                }
                                disabled={disabled}
                            />
                        </div>
                    )}
                </>
            )}

            {value.type === 'CLIENT' && (
                <ClientSelector
                    value={value.clientId || null}
                    onChange={(id) => onChange({ ...value, clientId: id || null })}
                    disabled={disabled}
                />
            )}

            {value.type === 'PARTNER' && (
                <PartnerSelector
                    value={value.partnerId || null}
                    onChange={(id) =>
                        onChange({ ...value, partnerId: id || null })
                    }
                    disabled={disabled}
                />
            )}

            {value.type === 'PROFESSIONAL' && (
                <ProfessionalSearchSelector
                    value={value.professionalId || null}
                    onChange={(id) =>
                        onChange({ ...value, professionalId: id || null })
                    }
                    disabled={disabled}
                />
            )}

            {value.type === 'HOSPITAL' && (
                <HospitalSearchSelector
                    value={value.hospitalId || null}
                    onChange={(id) =>
                        onChange({ ...value, hospitalId: id || null })
                    }
                    disabled={disabled}
                />
            )}

            {value.type === 'USER' && (
                <UserSelector
                    value={value.userId || null}
                    onChange={(id) => onChange({ ...value, userId: id || null })}
                    disabled={disabled}
                />
            )}

            {value.type === 'EMPLOYEE' && (
                <EmployeeSelector
                    value={value.employeeId || null}
                    onChange={(id) =>
                        onChange({ ...value, employeeId: id || null })
                    }
                    disabled={disabled}
                />
            )}

            {hint && (
                <div className="alert alert-info text-xs py-2 px-3">
                    <MdInfoOutline className="text-base" />
                    <span>{hint}</span>
                </div>
            )}
        </div>
    )
}
